import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { PageContainer } from '../components/PageContainer';
import { Heading1, BodyText } from '../components/ui/Typography';
import { WineryGroup, CompactWineryGroup, ExperiencesEmptyState } from '../components/Experiences';
import { MaxPriceSlider } from '../components/Experiences/MaxPriceSlider';
import { ReservationPolicyToggle } from '../components/Experiences/ReservationPolicyToggle';
import { RegionSearch } from '../components/SearchPage/RegionSearch';
import { MapPanel, MobileMapButton, MobileMapOverlay } from '../components/SearchPage';
import { SearchResultsWithMap } from '../components/SearchResultsWithMap';
import type { Region, CityLocation } from '../components/SearchPage/RegionSearch';
import type { ReservationPolicy } from '@wineryxplorer/shared';
import { useSearchScope } from '../hooks/useSearchScope';
import { SearchScopePill } from '../components/search/SearchScopePill';

const WIDEN_RADIUS_METERS = 100_000;

const parseFloat2 = (s: string | null) => {
  if (!s) return null;
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : null;
};

export const Experiences = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const distanceLocale: 'imperial' | 'metric' = 'imperial';

  // Filter URL state (Experiences-specific)
  const radiusOverride = parseFloat2(searchParams.get('r'));
  const maxPriceUsd = parseFloat2(searchParams.get('maxPrice')) ?? undefined;
  const wineClubOnly = searchParams.get('wineClub') === '1' ? true : undefined;
  const showMobileMap = searchParams.get('map') === '1';
  const reservationParam = searchParams.get('resv');
  const reservationPolicies = reservationParam
    ? (reservationParam.split(',') as ReservationPolicy[])
    : undefined;

  // Scope state and handlers from shared hook
  const {
    selectedRegion,
    selectedCity,
    locationParam,
    boundsParam,
    scopePill,
    isLocating,
    geoError,
    handleRegionChange,
    handleCitySearch,
    handleUseMyLocation,
    pendingMapBounds,
    hasUncommittedPan,
    mapCenter,
    mapZoom,
    handleMapBoundsChanged,
    handleCommitPendingBounds,
  } = useSearchScope();

  const updateUrl = useCallback(
    (overrides: Record<string, string | undefined>) => {
      const next = new URLSearchParams(searchParams);
      for (const [k, v] of Object.entries(overrides)) {
        if (v === undefined) next.delete(k);
        else next.set(k, v);
      }
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  // Wrap hook handlers to also clear the radius-override param on scope change.
  const onRegionChange = useCallback(
    (region: Region | null) => {
      handleRegionChange(region);
      updateUrl({ r: undefined });
    },
    [handleRegionChange, updateUrl]
  );

  const onCitySearch = useCallback(
    (city: CityLocation) => {
      handleCitySearch(city);
      updateUrl({ r: undefined });
    },
    [handleCitySearch, updateUrl]
  );

  const onUseMyLocation = useCallback(() => {
    handleUseMyLocation(() => {
      updateUrl({ r: undefined });
    });
  }, [handleUseMyLocation, updateUrl]);

  const handleWiden = () => {
    updateUrl({ r: String(WIDEN_RADIUS_METERS) });
  };

  const handleResetFilters = () => {
    updateUrl({ maxPrice: undefined, wineClub: undefined, resv: undefined });
  };

  const handleMaxPriceChange = (next: number | undefined) => {
    updateUrl({ maxPrice: next === undefined ? undefined : String(next) });
  };

  const handleReservationPoliciesChange = (next: ReservationPolicy[] | undefined) => {
    updateUrl({ resv: !next || next.length === 0 ? undefined : next.join(',') });
  };

  // Derive search location from the hook's locationParam, applying radius override if set.
  const searchLocation = useMemo(() => {
    if (!locationParam) return null;
    return {
      lat: locationParam.lat,
      lng: locationParam.lng,
      radiusMeters: radiusOverride ?? locationParam.radius,
    };
  }, [locationParam, radiusOverride]);

  const filtersAreNonDefault =
    maxPriceUsd !== undefined || wineClubOnly === true || (reservationPolicies?.length ?? 0) > 0;

  const queryInput = useMemo(() => {
    if (!searchLocation && !boundsParam) return null;
    return {
      location: searchLocation ?? undefined,
      bounds: boundsParam,
      maxPriceUsd,
      wineClubOnly,
      reservationPolicies,
    };
  }, [searchLocation, boundsParam, maxPriceUsd, wineClubOnly, reservationPolicies]);

  const [highlightedVenueId, setHighlightedVenueId] = useState<string | null>(null);

  const { data, isLoading, error } = trpc.tastingExperience.searchByLocation.useQuery(queryInput!, {
    enabled: queryInput !== null,
  });

  const mapVenues = useMemo(() => {
    if (!data) return [];
    return data.groups
      .filter((g) => g.venue.coordinates !== null)
      .map((g) => ({
        _id: g.venue.id,
        name: g.venue.name,
        venueType: 'winery',
        address: {
          city: g.venue.city ?? undefined,
          state: g.venue.state ?? undefined,
          coordinates: g.venue.coordinates!,
        },
        priceRange: null,
        firstHeroImage: null,
        logoImage: null,
      }));
  }, [data]);

  const { data: venuesNearby } = trpc.venue.search.useQuery(
    {
      location: searchLocation
        ? {
            lat: searchLocation.lat,
            lng: searchLocation.lng,
            radius: searchLocation.radiusMeters,
          }
        : undefined,
      limit: 20,
      offset: 0,
      sortBy: 'distance',
      sortOrder: 'asc',
      fallbackCount: 0,
    },
    {
      enabled:
        queryInput !== null &&
        data !== undefined &&
        data.groups.length === 0 &&
        data.venuesWithoutTastingsCount > 0,
    }
  );

  // One MapPanel node shared by the desktop sidebar and the mobile overlay.
  const mapPanel = (
    <MapPanel
      venues={mapVenues}
      onBoundsChanged={handleMapBoundsChanged}
      highlightedVenueId={highlightedVenueId}
      onPinClick={setHighlightedVenueId}
      center={mapCenter}
      zoom={mapZoom}
      topCenterOverlay={
        hasUncommittedPan && pendingMapBounds ? (
          <button
            type="button"
            onClick={handleCommitPendingBounds}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-700 rounded-lg shadow-md hover:bg-primary-800 transition-colors"
          >
            Search this area
          </button>
        ) : null
      }
    />
  );

  return (
    <PageContainer>
      <div className="px-0 md:px-4 pt-2 pb-4">
        <div className="px-3 sm:px-0 mb-4">
          <Heading1>Experiences</Heading1>
          <BodyText className="mt-2 text-gray-600">
            Tastings, flights, and bookable visits — search by region, city, or your current
            location.
          </BodyText>
        </div>

        {/* Same search widget as the Wineries page */}
        <div className="px-3 sm:px-0 mb-4">
          <RegionSearch
            selectedRegion={selectedRegion}
            selectedCity={selectedCity}
            onRegionChange={onRegionChange}
            onCitySearch={onCitySearch}
            onUseMyLocation={onUseMyLocation}
            isLocating={isLocating}
            geoError={geoError}
          />
        </div>

        {/* Active-scope pill */}
        {scopePill && (
          <div className="px-3 sm:px-0 mb-4">
            <SearchScopePill label={scopePill.label} onDismiss={scopePill.onDismiss} />
          </div>
        )}

        {/* Tasting-side filters (only when a location is set) */}
        {queryInput !== null && (
          <div className="px-3 sm:px-0 mb-6 flex flex-wrap gap-2 text-xs font-sans tracking-widest uppercase">
            <button
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                if (wineClubOnly) next.delete('wineClub');
                else next.set('wineClub', '1');
                setSearchParams(next);
              }}
              className={`px-3 py-1.5 rounded-full border ${
                wineClubOnly
                  ? 'bg-tertiary-100 border-tertiary-300 text-tertiary-800'
                  : 'bg-gray-50 border-gray-200 text-gray-500'
              }`}
            >
              Wine club only
            </button>
            <MaxPriceSlider value={maxPriceUsd} onChange={handleMaxPriceChange} />
            <ReservationPolicyToggle
              value={reservationPolicies}
              onChange={handleReservationPoliciesChange}
            />
          </div>
        )}

        {/* Loading */}
        {queryInput !== null && isLoading && (
          <div className="px-3 sm:px-0 py-16 text-center">
            <div className="animate-spin inline-block rounded-full h-8 w-8 border-b-2 border-primary-700" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="px-3 sm:px-0 py-16 text-center text-error">
            Failed to load tastings. Please try again.
          </div>
        )}

        {/* Empty: no location yet */}
        {queryInput === null && (
          <ExperiencesEmptyState
            state={{ kind: 'no-location' }}
            onUseMyLocation={onUseMyLocation}
          />
        )}

        {/* Empty: filters too restrictive */}
        {data && data.groups.length === 0 && filtersAreNonDefault && (
          <ExperiencesEmptyState
            state={{ kind: 'filters-too-restrictive', onReset: handleResetFilters }}
            distanceLocale={distanceLocale}
          />
        )}

        {/* Empty: wineries without tastings */}
        {data &&
          data.groups.length === 0 &&
          !filtersAreNonDefault &&
          data.venuesWithoutTastingsCount > 0 && (
            <ExperiencesEmptyState
              state={{
                kind: 'wineries-without-tastings',
                venues: (venuesNearby?.items ?? []).map((v) => ({
                  _id: v._id ?? '',
                  name: v.name,
                  venueType: v.venueType,
                  address: v.address,
                  priceRange: v.priceRange ?? null,
                  primaryAtmosphere: v.primaryAtmosphere ?? null,
                  amenities: v.amenities ?? [],
                  firstHeroImage: v.firstHeroImage ?? null,
                  logoImage: v.logoImage ?? null,
                  distance: v.distance,
                })) as never,
                userLocation: searchLocation
                  ? { lat: searchLocation.lat, lng: searchLocation.lng }
                  : null,
              }}
              distanceLocale={distanceLocale}
            />
          )}

        {/* Empty: no wineries at all */}
        {data &&
          data.groups.length === 0 &&
          !filtersAreNonDefault &&
          data.venuesWithoutTastingsCount === 0 && (
            <ExperiencesEmptyState
              state={{
                kind: 'no-wineries',
                onWiden: handleWiden,
                widenRadiusMeters: WIDEN_RADIUS_METERS,
              }}
              distanceLocale={distanceLocale}
            />
          )}

        {/* Results */}
        {data && data.groups.length > 0 && (
          <>
            <div className="px-3 sm:px-0 mb-3 flex justify-end lg:hidden">
              <MobileMapButton onClick={() => updateUrl({ map: '1' })} />
            </div>
            <SearchResultsWithMap map={mapPanel}>
              {data.groups.map((g) => (
                <WineryGroup key={g.venue.id} group={g as never} distanceLocale={distanceLocale} />
              ))}
            </SearchResultsWithMap>
          </>
        )}

        {/* Mobile full-screen map overlay. Gated on scope, not results, so a
            "Search this area" that finds nothing doesn't yank the map away. */}
        {showMobileMap && queryInput !== null && (
          <MobileMapOverlay map={mapPanel} onClose={() => updateUrl({ map: undefined })}>
            <div className="space-y-3">
              {data?.groups.map((g) => (
                <CompactWineryGroup
                  key={g.venue.id}
                  group={g as never}
                  highlighted={highlightedVenueId === g.venue.id}
                  distanceLocale={distanceLocale}
                />
              ))}
              {(!data || data.groups.length === 0) && (
                <p className="text-sm text-gray-500 text-center py-4">
                  {isLoading ? 'Searching...' : 'No tastings found in this area'}
                </p>
              )}
            </div>
          </MobileMapOverlay>
        )}
      </div>
    </PageContainer>
  );
};
